import React, { useEffect } from "react";
import { useForm, FormProvider, useFormContext } from "react-hook-form";
import {
  Block,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
  BlockDes,
  PreviewCard,
} from "../../components/Component";
import { RegistrationForm } from "./RegistrationForm";
import supplierApis from "../../api/master/supplier";
import Swal from "sweetalert2";

/**
 * Formats RegistrationForm data into the required API payload structure
 * Only includes fields that are present in the RegistrationForm
 */
export const formatRegistrationDataForApi = (formData: any) => {
  // Build supplierData object with only RegistrationForm fields
  const supplierData: any = {
    // Business/Legal Information
    bussinessType: formData.bussinessType || "",
    gstin: formData.gstin || "",
    pan: formData.pan || "",

    // General Information
    supplierName: formData.supplierName || "",
    tradeName: formData.tradeName || "",
    brandName: formData.brandName || "",
    deliveryLeadDays: formData.deliveryLeadDays || "",

    // Contact Information
    contactPerson: formData.contactPerson || "",
    phoneNumber: formData.phoneNumber || "",
    email: formData.email || "",

    // Address Information
    country: formData.country || "",
    state: formData.state || "",
    district: formData.district || "",
    city: formData.city || "",
    area: formData.area || "",
    pincode: formData.pincode || "",
    addressLine1: formData.addressLine1 || "",
    addressLine2: formData.addressLine2 || "",

    // Required empty arrays for API structure
    additionalContacts: [],
    materials: [],
    categories: [],
    supplyStates: [],

    // Required fields with defaults
    applicationAccess: "No",
    paymentTerms: "",
    referenceID: "",
    lastFYTurnover: 0,
    incorporationDate: "",
    cin: "",
    companyName: "",
    mailingAddressLine1: "",
    accountHolderName: "",
    accountNumber: "",
    bankName: "",
    branch: "",
    ifscCode: "",
  };

  return {
    files: [],
    filesData: {
      newfiledata: [],
      deletedFileData: [],
      updatedFileData: [],
    },
    supplierData,
  };
};

interface SupplierRegistrationProps {
  onComplete: (data: any) => void;
  onBack?: () => void;
  initialEmailOrPhone?: string;
  initialName?: string;
  buyerOrgId?: number | null;
}

const SupplierRegistration: React.FC<SupplierRegistrationProps> = ({
  onComplete,
  onBack,
  initialEmailOrPhone,
  initialName,
  buyerOrgId,
}) => {
  const formMethods = useForm({
    mode: "onChange",
    defaultValues: {
      email: initialEmailOrPhone?.includes("@") ? initialEmailOrPhone : "",
      phoneNumber: !initialEmailOrPhone?.includes("@")
        ? initialEmailOrPhone
        : "",
      contactPerson: initialName || "",
    },
  });

  useEffect(() => {
    // Pre-fill email or phone based on what was provided
    if (initialEmailOrPhone) {
      if (initialEmailOrPhone.includes("@")) {
        formMethods.setValue("email", initialEmailOrPhone);
      } else {
        formMethods.setValue("phoneNumber", initialEmailOrPhone);
      }
    }
    if (initialName) {
      formMethods.setValue("contactPerson", initialName);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialEmailOrPhone, initialName]);

  const handleSubmit = async (formData?: any) => {
    let response: any = null;
    try {
      // Get all form data - use passed data or get from form
      const allFormData: any = formData || formMethods.getValues();

      // Create FormData for multipart/form-data
      const supplierFormData = new FormData();

      // Build supplierData object
      const supplierData: any = {
        // Business/Legal Information
        bussinessType: allFormData.bussinessType || "",
        gstin: allFormData.gstin || "",
        pan: allFormData.pan || "",

        // General Information
        supplierName: allFormData.supplierName || "",
        tradeName: allFormData.tradeName || "",
        brandName: allFormData.brandName || "",
        deliveryLeadDays: allFormData.deliveryLeadDays || "",

        // Contact Information
        contactPerson: allFormData.contactPerson || "",
        phoneNumber: allFormData.phoneNumber || "",
        email: allFormData.email || "",

        // Address Information
        country: allFormData.country || "",
        state: allFormData.state || "",
        district: allFormData.district || "",
        city: allFormData.city || "",
        area: allFormData.area || "",
        pincode: allFormData.pincode || "",
        addressLine1: allFormData.addressLine1 || "",
        addressLine2: allFormData.addressLine2 || "",

        // Required empty arrays for API structure
        additionalContacts: [],
        materials: [],
        categories: [],
        supplyStates: [],

        // Required fields with defaults
        applicationAccess: "No",
        paymentTerms: "",
        referenceID: "",
        lastFYTurnover: 0,
        incorporationDate: "",
        cin: "",
        companyName: "",
        mailingAddressLine1: "",
        accountHolderName: "",
        accountNumber: "",
        bankName: "",
        branch: "",
        ifscCode: "",

        // Buyer Organization ID (from link verification)
        buyerOrgId: buyerOrgId,
      };

      // Append supplierData as JSON string
      supplierFormData.append("supplierData", JSON.stringify(supplierData));

      // Append filesData as array (empty array if no files)
      const filesData: any[] = [];
      supplierFormData.append("filesData", JSON.stringify(filesData));

      // Call the API to create public supplier
      response = await supplierApis.createPublicSupplier(supplierFormData);

      // Check if response has error (from makeUnauthenticatedApiCall error handling)
      if (response instanceof Error) {
        throw response;
      }

      if (response?.status) {
        // Pass both original form data and API response to onComplete
        onComplete({
          ...allFormData,
          apiResponse: response,
        });
      } else {
        // Handle error response from API
        const errorMessage =
          response?.message || "Failed to create supplier. Please try again.";
        Swal.fire({
          icon: "error",
          title: "Error",
          text: errorMessage,
          confirmButtonText: "OK",
          confirmButtonColor: "#3085d6",
        });
        // Don't navigate away - user should stay on registration page to fix errors
      }
    } catch (error: any) {
      console.error("Error creating supplier:", error);
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        response?.message ||
        "Failed to create supplier. Please try again.";
      Swal.fire({
        icon: "error",
        title: "Error",
        text: errorMessage,
        confirmButtonText: "OK",
        confirmButtonColor: "#3085d6",
      });
      // Don't navigate away - user should stay on registration page to fix errors
    }
  };

  return (
    <>
      <BlockHead size="lg">
        <BlockHeadContent>
          <BlockTitle tag="h4">Supplier Registration</BlockTitle>
          <BlockDes>
            Please complete the registration form to proceed with the quotation.
          </BlockDes>
        </BlockHeadContent>
      </BlockHead>
      <div style={{ position: "relative", zIndex: 10, pointerEvents: "auto" }}>
        <Block>
          <PreviewCard
            className={"cardBox"}
            bodyClass="card-inner-xl"
            style={{ position: "relative", zIndex: 10, pointerEvents: "auto" }}
          >
            <div
              style={{
                position: "relative",
                zIndex: 10,
                pointerEvents: "auto",
              }}
            >
              <FormProvider {...formMethods}>
                <RegistrationForm onSubmit={handleSubmit} onBack={onBack} />
              </FormProvider>
            </div>
          </PreviewCard>
        </Block>
      </div>
    </>
  );
};

export default SupplierRegistration;
